import { useState } from "react";
import { Link } from "react-router-dom";
import { Zap, ArrowRight, X, MapPin } from "lucide-react";
import useCurrentState from "@/routes/useCurrentState";
import { hasFeature, statePath } from "@/lib/stateRegistry";

const DISMISS_KEY = "rc_tx_banner_dismissed";

/**
 * Texas ESID banner. Rendered ONLY on routes whose state has the `tdu_banner`
 * feature in STATE_REGISTRY (Texas today). It no longer depends on IP geo-detection:
 * the visitor's chosen state (the URL) decides, and a Texas visitor browsing /ohio
 * never sees it (blueprint acceptance criterion 6).
 */
export default function TexasBanner() {
  const state = useCurrentState();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  // The banner only exists to send visitors to the ESID lookup. While that tool is held (Design Spec F4: it returned a
  // generated number) the banner is held with it, so it never promises a lookup that is not there.
  if (!hasFeature(state, "tdu_banner") || !hasFeature(state, "esid_lookup") || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch {}
  };

  return (
    <div className="bg-gradient-to-r from-[#0A2540] via-[#0E3358] to-[#0A2540] text-white border-b border-[#0A2540] relative overflow-hidden" data-testid="texas-banner">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 py-3 flex items-center justify-between gap-4 relative">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <span className="hidden sm:flex w-9 h-9 bg-[#16A34A] rounded-lg items-center justify-center shrink-0" aria-hidden="true">
            <Zap className="w-4 h-4 text-white fill-white" strokeWidth={2.5} />
          </span>
          <div className="flex items-center gap-2 min-w-0">
            <span className="hidden md:inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-[#16A34A] bg-white/5 px-2 py-1 rounded-full shrink-0">
              <MapPin className="w-3 h-3" aria-hidden="true" /> Texas
            </span>
            <p className="text-sm md:text-base font-medium truncate">
              <span className="font-semibold">Need your Texas ESID?</span>{" "}
              <span className="text-slate-300 hidden sm:inline">Find your ESID here in seconds.</span>
              <span className="text-slate-300 sm:hidden">Find your ESID</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <Link to={statePath(state.slug, "esid-lookup")} className="bg-[#16A34A] hover:bg-[#15803D] text-white px-4 md:px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-1.5 whitespace-nowrap transition-colors" data-testid="texas-banner-cta">
            Find my ESID <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
          </Link>
          <button onClick={dismiss} className="w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center transition-colors" data-testid="texas-banner-dismiss" aria-label="Dismiss">
            <X className="w-4 h-4 text-slate-400" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}
